<script lang="ts" setup>
import type { PaymentStatusType } from '@/modules/cashIn/types/paymentStatusView.type';
import { GButton } from '@flash-global66/g-button';
import { GImage } from '@flash-global66/g-image';
import { Loader as GLoader } from '@flash-global66/g-loader';
import {
  PAYMENT_STATUS_CONNECTION_ERROR_DESCRIPTION,
  PAYMENT_STATUS_CONNECTION_ERROR_TITLE,
} from '@/shared/inswitch/constants/paymentStatus.constant';
import { usePaymentStatusResult } from './usePaymentStatusResult';

defineOptions({ name: 'PaymentStatusResult' });

defineProps<{
  status: PaymentStatusType;
  transactionNumber: string;
  formattedAmount: string;
}>();

defineEmits<{
  'go-home': [];
  'try-again': [];
}>();

const { secureFooterText } = usePaymentStatusResult();
</script>

<template>
  <div class="bg-gray-10 min-h-screen flex items-center justify-center relative">
    <GLoader v-if="status === 'loading'" />
    <div
      v-else-if="status === 'success'"
      class="flex flex-col gap-4 items-center p-0 w-[504px] absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
    >
      <div class="flex items-center justify-center overflow-hidden shrink-0">
        <GImage name="check" size="xl" />
      </div>
      <div class="flex flex-col gap-4 items-start text-center w-full">
        <div class="flex justify-center w-full">
          <span
            class="inline-flex items-center bg-grey-50 rounded-full py-0.5 px-2 text-primary-txt text-3 font-semibold"
          >
            Nº de transacción {{ transactionNumber }}
          </span>
        </div>
        <div
          v-if="formattedAmount"
          class="flex flex-col justify-center shrink-0 text-primary-txt text-9 font-semibold leading-[36px] w-full"
        >
          <p class="whitespace-pre-wrap">
            Ingresaste {{ formattedAmount }}
          </p>
        </div>
        <div class="flex flex-col justify-center shrink-0 text-5 text-secondary-txt w-full">
          <p class="leading-[24.358px] whitespace-pre-wrap">
            Tu dinero ya está disponible en tu cuenta
          </p>
        </div>
      </div>
      <GButton
        class="h-[42px] w-[400px]"
        variant="primary"
        data-test="button:entendido"
        @click="$emit('go-home')"
      >
        Entendido
      </GButton>
    </div>
    <div
      v-else-if="status === 'error'"
      class="flex flex-col gap-4 items-center p-0 w-[504px] absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
    >
      <div class="flex items-center justify-center overflow-hidden shrink-0">
        <GImage name="warning-sign-yellow" size="xl" />
      </div>
      <div class="flex flex-col gap-6 items-center text-center w-full whitespace-pre-wrap">
        <div class="flex flex-col justify-center shrink-0 text-primary-txt text-7 font-semibold leading-[32px] w-full">
          <p>Parece que hubo un problema</p>
        </div>
        <div class="flex flex-col justify-center shrink-0 text-secondary-txt text-4 font-medium leading-[24px] w-full">
          <p>
            No se pudo completar tu solicitud. Por favor, inténtalo de nuevo.
          </p>
        </div>
      </div>
      <div class="flex flex-col gap-4 items-center w-full">
        <GButton
          class="h-[42px] w-[400px]"
          variant="primary"
          data-test="button:intentar-de-nuevo"
          @click="$emit('try-again')"
        >
          Intentar de nuevo
        </GButton>
        <GButton
          class="h-[42px] w-[400px]"
          variant="tertiary"
          data-test="button:ir-al-inicio"
          @click="$emit('go-home')"
        >
          Ir al inicio
        </GButton>
      </div>
    </div>
    <div
      v-else-if="status === 'connectionError'"
      class="flex flex-col gap-4 items-center p-0 w-[504px] absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
    >
      <div class="flex items-center justify-center overflow-hidden shrink-0">
        <GImage name="disconnected-plug" size="3xl" />
      </div>
      <div class="flex flex-col gap-6 items-center text-center w-full whitespace-pre-wrap">
        <div class="flex flex-col justify-center shrink-0 text-primary-txt text-7 font-semibold leading-[32px] w-full">
          <p>{{ PAYMENT_STATUS_CONNECTION_ERROR_TITLE }}</p>
        </div>
        <div class="flex flex-col justify-center shrink-0 text-secondary-txt text-4 font-medium leading-[24px] w-full">
          <p>{{ PAYMENT_STATUS_CONNECTION_ERROR_DESCRIPTION }}</p>
        </div>
      </div>
      <div class="flex flex-col gap-4 items-center w-full">
        <GButton
          class="h-[42px] w-[400px]"
          variant="primary"
          data-test="button:intentar-de-nuevo"
          @click="$emit('try-again')"
        >
          Intentar de nuevo
        </GButton>
        <GButton
          class="h-[42px] w-[400px]"
          variant="tertiary"
          data-test="button:ir-al-inicio"
          @click="$emit('go-home')"
        >
          Ir al inicio
        </GButton>
      </div>
    </div>
    <div class="absolute bottom-0 left-1/2 transform -translate-x-1/2 mb-4">
      <div class="flex justify-center items-center text-gray-21 text-2">
        <fa-icon class="mr-2" :icon="['fas', 'lock-alt']" />
        <p>{{ secureFooterText }}</p>
      </div>
    </div>
  </div>
</template>
